// The document's outline, derived from the render model: its sections, and in
// each the blocks a reader navigates by — rules (numbered R1… in the order the
// stylesheet's clause counter numbers them), guardrails, examples, figures
// (diagrams, drawings, workflows). It drives the contents list at the top (an
// agenda: each section with what it holds), the back matter at the end (every
// rule at a glance), and the front page's outline navigator. Pure, like html.ts.
//
// Anchors are positional — `#r-3` is the third rule, `#g-1` the first guardrail,
// `#x-2` the second example, `#f-1` the first figure — so an unnamed block is
// still addressable without changing the markup; `resolveAnchor` finds them.
import type { Node, RenderModel } from "./project";
import { sentence } from "./icons";
import { workflowSvg } from "./figures";
import { visibleText } from "./md";

/** A workflow block is drawn when its body has steps to draw, as blocks.ts draws it. */
const drawn = (n: Extract<Node, { type: "block" }>) => n.kind === "workflow" && !!n.value && workflowSvg(n.value.source, n.title ?? n.id) !== null;

export type EntryKind = "rule" | "guardrail" | "example" | "figure" | "workflow";
export interface OutlineEntry { kind: EntryKind; anchor: string; label: string; text: string; tone?: string }
export interface OutlineSection { id?: string; level: number; text: string; entries: OutlineEntry[] }
export interface Outline { sections: OutlineSection[]; counts: Record<EntryKind, number> }

const RULES = new Set(["must", "always", "should", "avoid", "never", "may"]);
const plain = (html: string) => visibleText(html).replace(/\s+/g, " ").trim();
const clip = (s: string, n = 110) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

/** A rule's tone, as html.ts draws it. */
export function ruleTone(kind: string, label: string): string {
  const negated = kind === "avoid" || / NOT$/.test(label);
  if (kind === "never" || (kind === "must" && negated)) return "never";
  if (negated) return "avoid";
  return kind === "always" ? "must" : kind;
}

export function outline(model: Pick<RenderModel, "nodes">): Outline {
  const counts: Record<EntryKind, number> = { rule: 0, guardrail: 0, example: 0, figure: 0, workflow: 0 };
  const sections: OutlineSection[] = [{ level: 0, text: "Opening", entries: [] }];
  const add = (e: Omit<OutlineEntry, "anchor">, prefix: string, n: number) => sections[sections.length - 1].entries.push({ ...e, anchor: `${prefix}-${n}` });

  const prose = (html: string) => {
    // headings and figures, in document order
    const re = /<h([1-6]) id="([^"]*)"[^>]*>([\s\S]*?)<\/h\1>|<figure class="fig" data-fig="([a-z]+)"(?: aria-label="([^"]*)")?[^>]*>/g;
    for (let m; (m = re.exec(html)); ) {
      if (m[1]) sections.push({ id: m[2], level: Number(m[1]), text: plain(m[3]), entries: [] });
      else add({ kind: "figure", label: m[4] === "sketch" ? "Drawing" : "Diagram", text: m[5] ? plain(m[5]) : m[4] === "mermaid" ? "Mermaid diagram" : "" }, "f", ++counts.figure);
    }
  };

  const walk = (nodes: Node[]) => {
    for (const n of nodes) {
      switch (n.type) {
        case "markdown": prose(n.html); break;
        case "callout":
          if (RULES.has(n.kind)) add({ kind: "rule", label: `R${counts.rule + 1}`, text: clip(plain(n.prose ?? n.html)), tone: ruleTone(n.kind, n.label) }, "r", ++counts.rule);
          else if (n.kind === "guardrail") add({ kind: "guardrail", label: "Guardrail", text: clip(plain(n.prose ?? n.html)) }, "g", ++counts.guardrail);
          else if (n.kind === "example") add({ kind: "example", label: n.avoid ? "Don’t" : "Do", text: clip(n.title ?? plain(n.prose ?? n.html), 80), tone: n.avoid ? "never" : "may" }, "x", ++counts.example);
          break;
        case "block":
          if (drawn(n)) { counts.workflow++; add({ kind: "workflow", label: "Workflow", text: n.title ?? n.id ?? "workflow" }, "f", ++counts.figure); }
          walk(n.children);
          break;
      }
    }
  };
  walk(model.nodes);
  if (!sections[0].entries.length) sections.shift();
  return { sections, counts };
}

const SELECTORS: Record<string, string> = { r: ".rule", g: ".alert.tone-guardrail", x: "figure.ex", f: "figure.fig" };

/** The element an outline anchor points at, inside a rendered document. */
export function resolveAnchor(root: ParentNode, anchor: string): Element | null {
  const m = /^([rgxf])-(\d+)$/.exec(anchor);
  if (!m) return null;
  return root.querySelectorAll(SELECTORS[m[1]])[Number(m[2]) - 1] ?? null;
}

export const describeCounts = (c: Record<EntryKind, number>) =>
  [c.rule && `${c.rule} rule${c.rule === 1 ? "" : "s"}`, c.guardrail && `${c.guardrail} guardrail${c.guardrail === 1 ? "" : "s"}`, c.example && `${c.example} example${c.example === 1 ? "" : "s"}`, c.figure && `${c.figure} figure${c.figure === 1 ? "" : "s"}`].filter(Boolean).join(" · ");

export { sentence };
